import classNames from 'classnames';
import Icon, { IconNames } from 'components/Icon';
import Loader from 'components/Loader';
import { ScreenLoader } from 'components/Loader/ScreenLoader';
import { showToast } from 'components/Toast';
import { ENV_CONFIG } from 'config/environment';
import { getIsRecipient } from 'config/permissions';
import colors from 'constants/colors';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAdminsState } from 'selectors/admins';
import {
  useCheckOnboardingStatusQuery,
  useGetAdminsRecipientsQuery,
  useStartOnboardingMutation,
} from 'services/adminsApi';
import { useGetImpactScoreEligibilityQuery } from 'services/impactScoreEligibilityApi';
import { useGetNextPayoutAmountQuery } from 'services/nextPayoutAmountApi';
import { useGetRecipientRaceQuery } from 'services/recipientRaceApi';
import { adminsOBCheckSuccess } from 'store/admins/slice';
import { OnboardingStatusOpts } from 'store/admins/types';
import { postAccountsLink } from 'store/recipients/actions';
import { OnboardingForm } from 'views/StripeOnboarding/OnboardingForm';
import { AccountSetupNeeded } from 'views/StripeOnboarding/AccountSetupNeeded';
import { PendingPayout } from './PendingPayout';
import { ProfileProgress } from './ProfileProgress';
import { PublicFundraisingLink } from './PublicFundraisingLink';
import { RecipientBadgesCard } from './RecipientBadgesCard';
import RecipientsFAQ from './RecipientsFAQ';
import { datadogLogs } from '@datadog/browser-logs';

const SCREEN: DashboardScreen = 'recipients_dashboard';

const LOADING_TEXT = 'Setting up your Stripe account...';

export const RecipientsDashboard = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [screenLoadingText, setScreenLoadingText] = useState(LOADING_TEXT);

  const { selectedRecipient, activeRole } = useAppSelector(state => getAdminsState(state));

  // Fetch recipients using RTK Query
  const { data: recipients = [], isLoading: isLoadingRecipients } = useGetAdminsRecipientsQuery(
    undefined,
    {
      skip: !activeRole || !getIsRecipient(activeRole),
    }
  );

  const hasRecipientAccess = recipients && recipients.length > 0;
  const noRecipientsAccess = recipients && recipients.length === 0;

  const recipientId = useMemo(() => {
    return selectedRecipient?.id || null;
  }, [selectedRecipient?.id]);

  const [isStartingOnboarding, setIsStartingOnboarding] = useState(false);
  const hasLoadedOnboardingOnce = useRef(false);

  /* --- RTK QUERY --*/
  const {
    // Use currentData instead of data so OBCheck is undefined while a new recipient's fetch is
    // in-flight. With `data`, RTK Query returns stale results from the previous recipient during
    // the transition, which could let a user bypass the onboarding gate if they switch from a
    // recipient with onboarding_form_complete=true to one where it is false.
    currentData: OBCheck,
    isLoading: isCheckingOB, // for initial fetch
    isFetching: isFetchingOB, // true on any fetch
  } = useCheckOnboardingStatusQuery(recipientId || '', {
    skip: !recipientId || location.pathname === '/welcome',
    // Force refetch when this changes
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const isPrimaryContact = useMemo(
    () => OBCheck?.is_primary_contact,
    [OBCheck?.is_primary_contact]
  );

  const stripeOnboardingNotStarted =
    OBCheck?.onboarding_status === OnboardingStatusOpts.NOT_STARTED;

  const [startOnboarding] = useStartOnboardingMutation();

  const {
    data: nextPayoutData,
    isError: isPayoutError,
    isLoading: isPayoutLoading,
  } = useGetNextPayoutAmountQuery(
    { recipient_id: recipientId || '' },
    {
      skip: !recipientId || !hasRecipientAccess,
    }
  );

  const { currentData: eligibilityData, isLoading: isEligibilityLoading } =
    useGetImpactScoreEligibilityQuery(
      { recipient_id: recipientId || '' },
      {
        skip: !recipientId || !hasRecipientAccess,
      }
    );
  // One expression gates all three consumers — the card, the race lookup, and the column
  // widths. Gate only the card and a col-md-6 PublicFundraisingLink lands beside a col-md-12
  // sibling, breaking the row. It holds the values rather than a bare boolean because
  // TypeScript will not narrow the nullable fields through an aliased condition.
  const badgeCardValues =
    eligibilityData?.is_eligible &&
    eligibilityData.impact_score != null &&
    eligibilityData.ref_code &&
    eligibilityData.badge_url
      ? {
          impactScore: eligibilityData.impact_score,
          refCode: eligibilityData.ref_code,
          badgeUrl: eligibilityData.badge_url,
          badgeYear: eligibilityData.badge_year,
        }
      : null;
  const canShowBadges = !!badgeCardValues;

  const { data: raceData } = useGetRecipientRaceQuery(
    { recipient_id: recipientId || '' },
    {
      skip: !recipientId || !hasRecipientAccess || !canShowBadges,
    }
  );

  const payoutDate = useMemo(
    () =>
      nextPayoutData?.next_payout_date
        ? // Append 'T00:00:00' so the date-only value is parsed as midnight, avoiding timezone shifts that could move it to the previous/next day.
          new Date(nextPayoutData.next_payout_date + 'T00:00:00')
        : null,
    [nextPayoutData?.next_payout_date]
  );

  useEffect(() => {
    if (OBCheck) {
      dispatch(adminsOBCheckSuccess(OBCheck));
      if (!hasLoadedOnboardingOnce.current) {
        hasLoadedOnboardingOnce.current = true;
      }
    }
  }, [OBCheck, dispatch]);

  const triggeredRecipientsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (isCheckingOB || isFetchingOB || !OBCheck) {
      return;
    }
    if (noRecipientsAccess) {
      return;
    }

    const shouldStartOnboarding =
      OBCheck &&
      OBCheck?.is_primary_contact &&
      OBCheck?.onboarding_form_complete &&
      stripeOnboardingNotStarted;

    if (OBCheck && OBCheck.is_tos_accepted === false) {
      navigate('/welcome', { replace: true });
      return;
    } else if (selectedRecipient && triggeredRecipientsRef.current.has(selectedRecipient.id)) {
      return;
    } else if (
      /** Trigger stripe onboarding window if user is the primary contact and Recipient does not have a Stripe account */
      shouldStartOnboarding
    ) {
      // Mark as triggered before attempting
      if (selectedRecipient?.id) {
        triggeredRecipientsRef.current.add(selectedRecipient.id);
        setIsStartingOnboarding(true);
      }
      (async () => {
        if (!selectedRecipient?.id) return;

        try {
          const response = await startOnboarding({
            recipient_id: selectedRecipient.id,
            onboarding_status: OBCheck.onboarding_status,
          }).unwrap();

          if (response?.url) {
            const win = window.open(response.url, '_blank', 'noopener,noreferrer');
            if (win) win.opener = null;
          }
        } catch (error: any) {
          triggeredRecipientsRef.current.delete(selectedRecipient.id);
          datadogLogs.logger.error('Failed to start onboarding:', {}, error as Error);
          showToast(error?.data?.message || 'Failed to start onboarding', 'error', error?.status);
          setScreenLoadingText(LOADING_TEXT);
        } finally {
          // Onboarding call complete
          setIsStartingOnboarding(false);
        }
      })();
    }
  }, [
    OBCheck?.is_primary_contact,
    OBCheck?.onboarding_status,
    OBCheck?.onboarding_form_complete,
    OBCheck?.is_tos_accepted,
    stripeOnboardingNotStarted,
    selectedRecipient?.id,
    startOnboarding,
    dispatch,
  ]);

  const hasPrimaryContact = useMemo(
    () => OBCheck?.has_primary_contact,
    [OBCheck?.has_primary_contact]
  );
  const stripeOnboardingComplete = OBCheck?.onboarding_status === OnboardingStatusOpts.COMPLETE;
  const stripeOnboardingPending =
    OBCheck?.onboarding_status === OnboardingStatusOpts.REQUIREMENTS_PENDING ||
    OBCheck?.onboarding_status === OnboardingStatusOpts.IN_PROGRESS;
  const pendingOathCheck =
    stripeOnboardingComplete && hasPrimaryContact && !OBCheck?.is_stripe_authorized;

  const stripeOnboardingMessage =
    'Your Stripe Account has pending requirements. You will not be able to accept payments via Stripe until onboarding has been completed.';

  const getIneligibilityReason = () => {
    const conditions = [
      {
        check: !hasRecipientAccess,
        heading: 'No Recipients Available',
        body: (
          <>
            <span>
              You don&apos;t have access to any recipients at this time. Please contact your
              administrator or{' '}
              <Link
                to={`mailto:${ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}`}
                className={'recipient-dash'}
                target={'_blank'}
                rel="noopener noreferrer"
              >
                {ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}
              </Link>{' '}
              for assistance.
            </span>
          </>
        ),
      },
      {
        check: stripeOnboardingComplete && pendingOathCheck,
        heading: 'Pending',
        body: (
          <>
            <Icon name={IconNames.ArrowsCircle} color={colors.oathBlue} size={30} />
            <span className="body-text">Your account is under review. We&apos;ll be in touch!</span>
          </>
        ),
      },
      {
        check: stripeOnboardingNotStarted,
        heading: 'Stripe Onboarding Not Started',
        body: (
          <>
            Your Stripe account needs to be set up by your primary contact. You will not be able to
            accept payments via Stripe until your primary contact sets up the account. Please ask
            your primary contact to login to the dashboard to complete the onboarding process.
          </>
        ),
      },
    ];

    return conditions.find(condition => condition.check) || null;
  };

  const getAccountUpdateWarning = () => {
    const conditions = [
      {
        check: !hasPrimaryContact && stripeOnboardingPending,
        heading: 'Missing Primary Contact: Account Setup Not Complete',
        body: (
          <>
            Your account needs to be set up by your primary contact, which is missing for your
            account. You will not be able to accept payments via Stripe until your primary contact
            sets up the account. Please contact your administrator or{' '}
            <Link
              to={`mailto:${ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}`}
              className={'recipient-dash'}
              target={'_blank'}
              rel="noopener noreferrer"
            >
              {ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}
            </Link>{' '}
            to provide the primary contact&apos;s information .
          </>
        ),
      },
      {
        check: !hasPrimaryContact && stripeOnboardingComplete,
        heading: 'Missing Primary Contact',
        body: (
          <>
            {' '}
            Your organization is missing a Primary Contact. Only the Primary Contact is authorized
            to update the Stripe account and complete Stripe onboarding. Please contact your
            administrator or{' '}
            <Link
              to={`mailto:${ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}`}
              className={'recipient-dash'}
              target={'_blank'}
              rel="noopener noreferrer"
            >
              {ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}
            </Link>{' '}
            to provide the primary contact&apos;s information .
          </>
        ),
      },
      {
        check: hasPrimaryContact && !isPrimaryContact && stripeOnboardingPending,
        heading: 'Stripe Onboarding Incomplete',
        body: (
          <>
            {' '}
            Your primary contact must complete the Stripe Onboarding process before you can accept
            Stripe as a payment method. They can complete the process by logging into the{' '}
            <Link
              to={`{ENV_CONFIG().SITE_URL}/dashboard`}
              className={'recipient-dash'}
              target={'_blank'}
              rel="noopener noreferrer"
            >
              Oath Admin Dashboard
            </Link>
          </>
        ),
      },
    ];

    return conditions.find(condition => condition.check) || null;
  };

  const ineligibilityReason = getIneligibilityReason();
  const accountUpdateWarning = getAccountUpdateWarning();

  // Profile progress step completion
  // Step 1: onboarding form done AND Stripe account fully active
  const profileStep1Complete = !!(OBCheck?.onboarding_form_complete && stripeOnboardingComplete);
  // Step 2: Oath team has manually authorized the Stripe account
  const profileStep2Complete = !!OBCheck?.is_stripe_authorized;
  // Step 3: received at least one Stripe donation
  const profileStep3Complete = !!OBCheck?.has_stripe_transactions;

  // Show SubtleLoader when fetching OBCheck data (includes initial load and recipient changes)
  const showSubtleLoader = isCheckingOB || isFetchingOB || isLoadingRecipients;

  // Whether the onboarding form or dead-end screen should be shown
  const showOnboardingOverlay = !!(OBCheck && !OBCheck.onboarding_form_complete);

  // Toggle classes on the .wrapper ancestor so CSS can restyle the sidebar / panel.
  useEffect(() => {
    const wrapper = document.querySelector('.wrapper');
    if (!wrapper) return;
    wrapper.classList.add('wrapper--recipient-dashboard');
    if (showOnboardingOverlay) {
      wrapper.classList.add('wrapper--onboarding');
    } else {
      wrapper.classList.remove('wrapper--onboarding');
    }
    return () => {
      wrapper.classList.remove('wrapper--onboarding');
      wrapper.classList.remove('wrapper--recipient-dashboard');
    };
  }, [showOnboardingOverlay]);

  // Show ScreenLoader when starting the onboarding process (i.e. creating a Connect account)
  if (isStartingOnboarding) {
    return <ScreenLoader text={screenLoadingText} />;
  }

  if (recipients) {
    // Render if we have OBCheck data, no recipients access, or we're currently fetching
    const shouldRender = (OBCheck && hasRecipientAccess) || noRecipientsAccess || showSubtleLoader;

    if (shouldRender) {
      // If onboarding form is not complete, show the form (primary) or dead-end (non-primary)
      if (showOnboardingOverlay) {
        return (
          <div className="content content-mini">
            {OBCheck.is_primary_contact ? (
              recipientId && activeRole ? (
                <OnboardingForm recipientId={recipientId} />
              ) : (
                <Loader type="dots" />
              )
            ) : (
              <AccountSetupNeeded primaryContact={OBCheck.primary_contact} />
            )}
          </div>
        );
      }

      return (
        <>
          <div className="content content-mini recipients-dashboard">
            {!noRecipientsAccess && (
              <div className="row">
                <div className="col-md-12">
                  <ProfileProgress
                    stepOneComplete={showSubtleLoader ? false : profileStep1Complete}
                    stepTwoComplete={showSubtleLoader ? false : profileStep2Complete}
                    stepThreeComplete={showSubtleLoader ? false : profileStep3Complete}
                  />
                </div>
              </div>
            )}
            {!showSubtleLoader && (
              <>
                {ineligibilityReason && (
                  <>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="card">
                          <div
                            className={classNames('card-body recipients-card-content', {
                              'pending-manual-check': pendingOathCheck,
                            })}
                          >
                            <span className="h4-text">{ineligibilityReason.heading}</span>
                            {ineligibilityReason.body}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {/*Recipient has warnings on their account info  */}
                {accountUpdateWarning && (
                  <>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="card warning-message">
                          <div className="card-body recipients-card-content">
                            <span className="h4-text">{accountUpdateWarning.heading}</span>
                            <span className="body-text">{accountUpdateWarning.body}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {!ineligibilityReason && isPayoutError && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="alert alert-danger">
                        Unable to load payout information. Please try again later.
                      </div>
                    </div>
                  </div>
                )}

                {!ineligibilityReason &&
                  (isPayoutLoading ? (
                    <div className="col-md-12 pending-payout-placeholder" />
                  ) : (
                    nextPayoutData &&
                    nextPayoutData.next_payout_amount !== null &&
                    payoutDate && (
                      <PendingPayout
                        className="col-md-12"
                        payoutAmount={nextPayoutData.next_payout_amount}
                        payoutDate={payoutDate}
                      />
                    )
                  ))}

                <div className="row">
                  {!ineligibilityReason &&
                    selectedRecipient?.public_fundraising_query_str &&
                    (isEligibilityLoading ? (
                      <div className="col-md-12 recipient-badges-placeholder" />
                    ) : badgeCardValues ? (
                      <RecipientBadgesCard
                        className="col-md-12"
                        fundraisingUrl={`${ENV_CONFIG().PARTNER_PAGE}${encodeURIComponent(selectedRecipient.public_fundraising_query_str)}`}
                        recipientId={selectedRecipient.id}
                        recipientName={selectedRecipient.name}
                        photoId={selectedRecipient.photo_id}
                        raceDisplayInfo={raceData?.race_display_info ?? null}
                        impactScore={badgeCardValues.impactScore}
                        refCode={badgeCardValues.refCode}
                        badgeUrl={badgeCardValues.badgeUrl}
                        badgeYear={badgeCardValues.badgeYear}
                      />
                    ) : (
                      <PublicFundraisingLink
                        className="col-md-6"
                        url={`${ENV_CONFIG().PARTNER_PAGE}${encodeURIComponent(selectedRecipient.public_fundraising_query_str)}`}
                      />
                    ))}
                  {!ineligibilityReason && (
                    <div
                      className={classNames({
                        'col-md-6': !canShowBadges,
                        'col-md-12': canShowBadges,
                      })}
                    >
                      <div className="card">
                        <div className="card-body recipients-card-content">
                          <span className="h4-text">Reporting & Compliance</span>
                          <span className="body-text">
                            Have a question or need additional support? Reach out to{' '}
                            <Link
                              to={`mailto:${ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}`}
                              className={'recipient-dash'}
                              target={'_blank'}
                              rel="noopener noreferrer"
                            >
                              {ENV_CONFIG().RECIPIENTS_SUPPORT_EMAIL}
                            </Link>
                          </span>
                          <div className="row">
                            <>
                              <div className="col-md-8 grid top-card-left">
                                <span className="body-small-text color-gray">
                                  To download donor-level information associated with your recent
                                  payouts, please visit the Payout Reports section.
                                </span>
                                <button
                                  type="button"
                                  className="btn-stripe btn btn-secondary"
                                  onClick={() => {
                                    trackEvent('Payout Report Link Clicked', { screen: SCREEN });
                                    const win = window.open(
                                      `${ENV_CONFIG().SITE_URL}/payouts`,
                                      '_blank',
                                      'noopener,noreferrer'
                                    );
                                    if (win) win.opener = null;
                                  }}
                                >
                                  <Icon
                                    name={IconNames.StripeLogo}
                                    color="white"
                                    size={60}
                                    style={{ height: 28 }}
                                  />
                                </button>
                              </div>
                              <div className="col-md-2 padding0" />
                            </>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!selectedRecipient?.public_fundraising_query_str && (
                    <RecipientsFAQ
                      className={classNames({
                        'col-md-6': !ineligibilityReason,
                        'col-md-12': ineligibilityReason,
                      })}
                    />
                  )}
                </div>

                {!!selectedRecipient?.public_fundraising_query_str && (
                  <div className="row">
                    <RecipientsFAQ
                      className={classNames({
                        'col-md-6': !ineligibilityReason,
                        'col-md-12': ineligibilityReason,
                      })}
                    />
                  </div>
                )}

                {isPrimaryContact && (
                  <div className="row">
                    <div
                      className={classNames({
                        'col-md-6': isPrimaryContact,
                        'col-md-12': !isPrimaryContact,
                      })}
                    >
                      <div
                        className={classNames('card', {
                          'warning-message': isPrimaryContact && !stripeOnboardingComplete,
                        })}
                      >
                        <div className="card-body recipients-card-content">
                          <span className="h4-text">
                            {stripeOnboardingComplete
                              ? 'Update Connect Account'
                              : 'Complete Onboarding'}
                          </span>
                          <span className="body-text">
                            {stripeOnboardingComplete
                              ? ' Go to Stripe Account Management'
                              : stripeOnboardingMessage}
                          </span>

                          <div className="col-md-8 padding0">
                            <button
                              type="button"
                              className="btn-stripe btn btn-secondary"
                              onClick={async () => {
                                trackEvent('Stripe Onboarding Button Clicked', {
                                  screen: SCREEN,
                                  action: stripeOnboardingComplete
                                    ? 'account_update'
                                    : 'onboarding',
                                });
                                try {
                                  if (stripeOnboardingComplete) {
                                    // Use postAccountsLink for updates
                                    const response = await postAccountsLink(
                                      {
                                        recipient_id: selectedRecipient?.id || '',
                                        type: 'account_update',
                                      },
                                      activeRole
                                    );
                                    if (response.url) {
                                      const win = window.open(
                                        response.url,
                                        '_blank',
                                        'noopener,noreferrer'
                                      );
                                      if (win) win.opener = null;
                                    }
                                  } else {
                                    // Use startOnboarding for initial onboarding
                                    const response = await startOnboarding({
                                      recipient_id: selectedRecipient?.id || '',
                                      onboarding_status: OBCheck?.onboarding_status || '',
                                    }).unwrap();
                                    if (response?.url) {
                                      const win = window.open(
                                        response.url,
                                        '_blank',
                                        'noopener,noreferrer'
                                      );
                                      if (win) win.opener = null;
                                    }
                                  }
                                } catch (error: any) {
                                  showToast(
                                    error?.data?.message || error.message || 'An error occurred',
                                    'error',
                                    error?.response?.status || error?.status
                                  );
                                }
                              }}
                            >
                              <Icon
                                name={IconNames.StripeLogo}
                                color={colors.white}
                                size={60}
                                style={{ height: 28 }}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      );
    }
  }
});

RecipientsDashboard.displayName = 'RecipientsDashboard';
